import cn from "classnames"

import { Flex, Text } from "@/atoms"
import { NA_VALUE } from "@/const/default-values"
import * as ScanHistoryDomain from "@/domain/scan-history"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { InfoCircleOutlined, WarningOutlined } from "@/icons"
import { ShowMore } from "@/molecules/ShowMore"
import { StatusTag, type Status } from "@/molecules/StatusTag"

import styles from "./ScanResultCell.module.scss"

import type { ScanHistoryRecord } from "@/api/useScanHistory.types"
import type { TFunction } from "@/hooks/useTranslation"

export const ScanResultCell = (t: TFunction) =>
  function ScanResultCellComponent(status: Status, record: ScanHistoryRecord) {
    if (ScanHistoryDomain.isScanning(record)) return null

    const isScannedNA =
      ScanHistoryDomain.isCompleted(record) &&
      ScanHistoryDomain.noPolicyName(record)
    const statusKey = `governance.scanHistory.status.${record.policyResult || "na"}`
    const text = t(statusKey, {
      defaultValue: t("governance.scanHistory.status.na")
    })

    if (ScanHistoryDomain.isNA(record))
      return (
        <div className={styles.textWrapper}>
          <WarningOutlined className={cn(styles.icon, styles.iconError)} />
          <span>{t("governance.scanHistory.status.error")}</span>
        </div>
      )

    return (
      <span>
        <ShowMore
          id={`${record.scanId}-scan-result`}
          label={
            <ShowMore.Label.Component>
              {!isScannedNA && (
                <Flex
                  component="span"
                  wrap
                  align="center"
                  gap={8}
                  className={styles.tags}
                >
                  <StatusTag status={status} text={text} />
                  {record.policyExceptionStatus && (
                    <Text type="secondary" className={styles.exceptionState}>
                      {t(
                        `governance.scanHistory.policyExceptionState.${record.policyExceptionStatus}`
                      )}
                    </Text>
                  )}
                </Flex>
              )}
              {isScannedNA && (
                <div className={styles.textWrapper}>
                  <span>{t("governance.scanHistory.status.completed")}</span>
                  <InfoCircleOutlined
                    data-custom-rect-element={true}
                    className={cn(styles.icon, styles.iconInfo)}
                  />
                </div>
              )}
            </ShowMore.Label.Component>
          }
        >
          <ShowMore.Component classes={{ content: styles.content }}>
            <ShowMore.Description
              items={[
                {
                  label: t("governance.scanHistory.reason"),
                  value: record.reason || ""
                },
                {
                  label: `${capitalizeFirstLetter(t("governance.policies.protect"))} ${capitalizeFirstLetter(t("general.policy", { count: 1 }))}`,
                  value:
                    record.policyName ||
                    t(`${statusKey}Desc`, { defaultValue: NA_VALUE })
                },
                {
                  label: `${capitalizeFirstLetter(t("governance.policies.suppress"))} ${capitalizeFirstLetter(t("general.policy", { count: record.matchedSuppressionPolicies?.length || 0 }))}`,
                  value: record.matchedSuppressionPolicies?.join(", ") || ""
                }
              ].filter((item) => Boolean(item.value))}
            />
          </ShowMore.Component>
        </ShowMore>
      </span>
    )
  }
